import type { FC} from "react";
import React, { useState, useCallback, useMemo } from "react";
import { Select, Input, Button, Popover, Divider } from "antd";
import { FilterOutlined, DeleteOutlined, CloseOutlined, UndoOutlined } from "@ant-design/icons";

export interface RangeFilter {
  min: string;
  max: string;
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface NumberFilterConfig {
  key: string;
  label: string;
  symbol?: string;
}

export interface FilterConfig {
  stringFilters?: {
    [key: string]: {
      label: string;
      options: FilterOption[];
    };
  };
  numberFilters?: NumberFilterConfig[];
}

export interface FilterValues {
  [key: string]: string[] | RangeFilter;
}

interface FilterComponentProps {
  config: FilterConfig;
  onApplyFilters: (filters: FilterValues) => void;
}

const FilterComponent: FC<FilterComponentProps> = ({ config, onApplyFilters }) => {
  const initialFilters = useMemo<FilterValues>(() => {
    const filters: FilterValues = {};

    if (config.stringFilters) {
      Object.keys(config.stringFilters).forEach(key => {
        filters[key] = [];
      });
    }

    if (config.numberFilters) {
      config.numberFilters.forEach(filter => {
        filters[filter.key] = { min: "", max: "" };
      });
    }
    return filters;
  }, [config]);

  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [isOpen, setIsOpen] = useState(false);

  // Add function to check if there are active filters
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([_, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      const rangeFilter = value as RangeFilter;
      return rangeFilter.min !== "" || rangeFilter.max !== "";
    });
  }, [filters]);

  const handleReset = useCallback(() => {
    setFilters(initialFilters);
    onApplyFilters(initialFilters);
    setIsOpen(false);
  }, [initialFilters, onApplyFilters]);

  const handleApply = useCallback(() => {
    onApplyFilters(filters);
    setIsOpen(false);
  }, [filters, onApplyFilters]);

  const handleStringFilterChange = useCallback((field: string, values: string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: values,
    }));
  }, []);

  const handleRangeFilterChange = useCallback((field: string, value: string, type: "min" | "max") => {
    setFilters(prev => ({
      ...prev,
      [field]: {
        ...(prev[field] as RangeFilter),
        [type]: value,
      },
    }));
  }, []);

  const handleClearFilter = useCallback((field: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: Array.isArray(prev[field]) ? [] : { min: "", max: "" },
    }));
  }, []);

  const renderContent = () => (
    <div className="w-96 p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Filters</h3>
        <Button type="text" icon={<CloseOutlined />} onClick={() => setIsOpen(false)} />
      </div>

      <div className="space-y-2">
        {/* String Filters */}
        {config.stringFilters &&
          Object.entries(config.stringFilters).map(([key, filterConfig]) => (
            <div key={key} className="space-y-1">
              <label className="text-sm font-medium text-gray-700">{filterConfig.label}</label>

              <div className="flex items-center justify-between">
                <Select
                  mode="multiple"
                  value={filters[key] as string[]}
                  onChange={value => handleStringFilterChange(key, value)}
                  options={filterConfig.options}
                  className="w-full"
                  placeholder={`Select ${filterConfig.label.toLowerCase()}`}
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleClearFilter(key)}
                  disabled={Array.isArray(filters[key]) && (filters[key] as string[]).length <= 0}
                />
              </div>
            </div>
          ))}

        {/* Number Range Filters */}
        {config.numberFilters &&
          config.numberFilters.map(({ key, label, symbol }) => (
            <div key={key} className="space-y-1">
              <label className="text-sm font-medium text-gray-700">{label}</label>
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={(filters[key] as RangeFilter).min}
                    onChange={e => handleRangeFilterChange(key, e.target.value, "min")}
                    placeholder="Min"
                    prefix={symbol}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    value={(filters[key] as RangeFilter).max}
                    onChange={e => handleRangeFilterChange(key, e.target.value, "max")}
                    placeholder="Max"
                    prefix={symbol}
                    className="flex-1"
                  />
                </div>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handleClearFilter(key)}
                  disabled={!(filters[key] as RangeFilter).min && !(filters[key] as RangeFilter).max}
                />
              </div>
            </div>
          ))}
      </div>

      <Divider className="my-4" />
      <div className="flex justify-between gap-2">
        <Button icon={<UndoOutlined />} onClick={handleReset}>
          Reset
        </Button>
        <Button type="primary" onClick={handleApply}>
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <div className="inline-block">
      <Popover
        content={renderContent()}
        trigger="click"
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottomRight"
        overlayClassName="filter-popover"
      >
        <Button icon={<FilterOutlined />} className={`${hasActiveFilters ? "border-primary text-primary" : ""}`}>
          Filters
        </Button>
      </Popover>

      <style jsx global>{`
        .filter-popover .ant-popover-inner-content {
          padding: 0;
        }
        .filter-popover {
          min-width: 320px;
        }
      `}</style>
    </div>
  );
};

export default FilterComponent;









